import React, { useCallback, useEffect, useState } from "react";
import {
  ShieldCheck, MessageCircle, Upload, Camera, FileText, Loader2, PackageCheck,
  Image as ImageIcon, UserCheck, PencilLine, FilePlus2, History,
} from "lucide-react";
import { api, fmtDate } from "@/lib/apiClient";
import { DeskripsiEditor } from "@/components/DeskripsiEditor";
import { toast } from "sonner";

const stamp = (iso) =>
  iso ? new Date(iso).toLocaleString("id-ID", { dateStyle: "short", timeStyle: "short" }) : "";

export const QCBoard = () => {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(null);
  const [cari, setCari] = useState("");
  const [tab, setTab] = useState("belum");

  const load = useCallback(async () => {
    const { data } = await api.get("/qc-board");
    setRows(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openWa = async (jo, status) => {
    try {
      const { data } = await api.get(`/job-orders/${jo.id}/wa-link`, { params: { status } });
      window.open(data.link, "_blank");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal membuat pesan WhatsApp");
    }
  };

  const setDiterima = async (jo, kategori) => {
    setBusy(`${jo.id}-${kategori}`);
    try {
      await api.put(`/job-orders/${jo.id}/items/${encodeURIComponent(kategori)}`, {
        status: "DITERIMA",
      });
      toast.success(`${jo.jo_number} · ${kategori} → DITERIMA`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal update status");
    } finally {
      setBusy(null);
    }
  };

  const uploadProof = async (jo, file) => {
    if (!file) return;
    setBusy(jo.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/job-orders/${jo.id}/proof`, fd);
      toast.success("Bukti foto terunggah");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengunggah bukti");
    } finally {
      setBusy(null);
    }
  };

  const viewFile = async (id) => {
    try {
      const res = await api.get(`/files/${id}`, { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch {
      toast.error("Gagal membuka file");
    }
  };

  const allSelesai = (jo) =>
    (jo.items || []).length > 0 &&
    (jo.items || []).every((i) => ["SELESAI", "DITERIMA"].includes(i.status));

  const allDiterima = (jo) =>
    (jo.items || []).length > 0 && (jo.items || []).every((i) => i.status === "DITERIMA");

  const q = cari.trim().toLowerCase();
  const matchClient = (jo) => !q || (jo.client_name || "").toLowerCase().includes(q);
  const view = rows.filter((jo) => matchClient(jo) && (tab === "sudah" ? !!jo.qc_wa : !jo.qc_wa));
  const countBelum = rows.filter((jo) => matchClient(jo) && !jo.qc_wa).length;
  const countSudah = rows.filter((jo) => matchClient(jo) && !!jo.qc_wa).length;

  return (
    <div className="space-y-6" data-testid="qc-board">
      <div className="glass rounded-2xl p-6 md:p-8">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <ShieldCheck className="w-5 h-5 text-blue-400" />
            <div>
              <h3 className="font-display text-xl font-semibold">Papan QC</h3>
              <p className="text-xs text-slate-400 mt-1">
                Job order yang sudah selesai di produksi ·{" "}
                <span className="text-blue-300 font-semibold" data-testid="qc-total">
                  {view.length} JO
                </span>
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <input
              data-testid="qc-client-filter"
              placeholder="Filter nama client"
              value={cari}
              onChange={(e) => setCari(e.target.value)}
              className="h-10 rounded-xl bg-slate-900/70 border border-white/10 px-3 text-sm outline-none focus:ring-2 focus:ring-blue-500"
            />
            {[
              { key: "belum", label: `Belum Konfirmasi WA (${countBelum})` },
              { key: "sudah", label: `Sudah Konfirmasi WA (${countSudah})` },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                data-testid={`qc-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`h-10 rounded-xl px-4 text-[11px] font-semibold uppercase tracking-wider transition-colors duration-200 ${
                  tab === t.key
                    ? "bg-blue-600 text-white"
                    : "border border-white/10 bg-slate-900/70 text-slate-300 hover:bg-slate-800"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {view.length === 0 && (
        <div className="glass rounded-2xl p-10 text-center text-slate-500" data-testid="qc-empty">
          {tab === "belum"
            ? "Tidak ada JO yang menunggu konfirmasi WA."
            : "Belum ada JO yang sudah dikonfirmasi WA."}
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {view.map((jo) => {
          const items = jo.qc_items || [];
          const diterima = items.filter((i) => i.status === "DITERIMA").length;
          return (
            <div key={jo.id} className="glass rounded-2xl p-5 md:p-6" data-testid={`qc-jo-${jo.jo_number}`}>
              <div className="flex flex-wrap items-start justify-between gap-3 border-b border-white/10 pb-4">
                <div>
                  <div className="font-display text-lg font-semibold text-slate-100">
                    JO {jo.jo_number}{" "}
                    <span className="text-xs font-normal text-slate-500">· {fmtDate(jo.tanggal)}</span>
                  </div>
                  <div className="text-sm text-slate-300 mt-0.5">{jo.client_name}</div>
                  <div className="text-xs text-slate-500">{jo.client_phone || "-"}</div>
                </div>
                <span className="rounded-md border border-white/10 bg-slate-900/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-300">
                  {diterima}/{items.length} diterima
                </span>
                {jo.qc_wa && (
                  <span
                    data-testid={`qc-wa-flag-${jo.jo_number}`}
                    className="rounded-md border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-300"
                  >
                    WA terkonfirmasi · {jo.qc_wa_by || "-"}
                  </span>
                )}
              </div>

              <DeskripsiEditor
                joId={jo.id}
                joNumber={jo.jo_number}
                value={jo.deskripsi}
                onChanged={load}
                testIdPrefix="qc-desk"
              />

              <div className="mt-4 flex flex-wrap items-center gap-4">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Bukti foto</span>
                {jo.proof_file_id ? (
                  <button
                    data-testid={`qc-view-proof-${jo.jo_number}`}
                    onClick={() => viewFile(jo.proof_file_id)}
                    className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 transition-colors duration-200"
                  >
                    <FileText className="w-4 h-4" /> Lihat bukti
                  </button>
                ) : (
                  <span className="text-xs text-slate-600">Belum ada</span>
                )}
                <label
                  data-testid={`qc-upload-proof-${jo.jo_number}`}
                  className="inline-flex items-center gap-1.5 text-xs text-blue-300 hover:text-blue-200 cursor-pointer transition-colors duration-200"
                >
                  {busy === jo.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Upload file
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    className="hidden"
                    onChange={(e) => uploadProof(jo, e.target.files?.[0])}
                  />
                </label>
                <label
                  data-testid={`qc-camera-proof-${jo.jo_number}`}
                  className="inline-flex items-center gap-1.5 text-xs text-amber-300 hover:text-amber-200 cursor-pointer transition-colors duration-200"
                >
                  <Camera className="w-4 h-4" /> Kamera
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => uploadProof(jo, e.target.files?.[0])}
                  />
                </label>
              </div>

              <div
                className="mt-4 rounded-xl border border-white/5 bg-slate-950/50 p-3 space-y-1.5"
                data-testid={`qc-history-${jo.jo_number}`}
              >
                <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">
                  Riwayat aktivitas JO
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-300">
                  <FilePlus2 className="w-3.5 h-3.5 text-blue-300 shrink-0" />
                  Entri oleh <span className="font-semibold text-blue-200">{jo.created_by || "-"}</span>
                  <span className="text-slate-500">{stamp(jo.created_at)}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px] text-slate-300">
                  <UserCheck className="w-3.5 h-3.5 text-emerald-300 shrink-0" />
                  ACC <span className="font-semibold text-emerald-200">{jo.admin_status || "belum"}</span>
                  {jo.admin_by && (
                    <>
                      oleh <span className="font-semibold text-emerald-200">{jo.admin_by}</span>
                      <span className="text-slate-500">{stamp(jo.admin_at)}</span>
                    </>
                  )}
                </div>
                {(jo.edits || []).slice(-3).map((e) => (
                  <div key={`e-${e.at}-${e.by}`} className="flex items-start gap-2 text-[11px] text-slate-300">
                    <PencilLine className="w-3.5 h-3.5 text-amber-300 shrink-0 mt-0.5" />
                    <span>
                      Diedit oleh <span className="font-semibold text-amber-200">{e.by}</span>{" "}
                      <span className="text-slate-500">{stamp(e.at)}</span>
                      {e.fields?.length > 0 && (
                        <span className="text-slate-500"> · {e.fields.join(", ")}</span>
                      )}
                    </span>
                  </div>
                ))}
                {(jo.item_logs || []).slice(-6).reverse().map((l) => (
                  <div key={`l-${l.at}-${l.kategori}`} className="flex items-start gap-2 text-[11px] text-slate-300">
                    <History className="w-3.5 h-3.5 text-sky-300 shrink-0 mt-0.5" />
                    <span>
                      <span className="font-semibold text-sky-200">{l.kategori}</span> →{" "}
                      <span className="font-semibold text-slate-100">{l.status}</span> oleh{" "}
                      <span className="font-semibold text-sky-200">{l.by}</span>{" "}
                      <span className="text-slate-500">{stamp(l.at)}</span>
                    </span>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">
                <button
                  data-testid={`qc-wa-selesai-${jo.jo_number}`}
                  onClick={() => openWa(jo, "SELESAI")}
                  disabled={!allSelesai(jo)}
                  title={allSelesai(jo) ? "Kirim WA pesanan SELESAI" : "Aktif bila semua kategori sudah SELESAI"}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-blue-400/30 bg-blue-500/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-blue-200 hover:bg-blue-500/20 disabled:opacity-30 transition-colors duration-200"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WA Selesai
                </button>
                <button
                  data-testid={`qc-wa-diterima-${jo.jo_number}`}
                  onClick={() => openWa(jo, "DITERIMA")}
                  disabled={!allDiterima(jo)}
                  title={allDiterima(jo) ? "Kirim WA konfirmasi DITERIMA" : "Aktif bila semua kategori sudah DITERIMA"}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-30 transition-colors duration-200"
                >
                  <MessageCircle className="w-3.5 h-3.5" /> WA Diterima
                </button>
              </div>

              <div className="mt-4 space-y-3">
                {items.map((it) => {
                  const k = `${jo.id}-${it.kategori}`;
                  const isBusy = busy === k;
                  const done = it.status === "DITERIMA";
                  return (
                    <div
                      key={it.kategori}
                      data-testid={`qc-item-${jo.jo_number}-${it.kategori}`}
                      className={`rounded-xl border p-3.5 transition-colors duration-200 ${
                        done ? "border-emerald-400/20 bg-emerald-500/5" : "border-white/10 bg-slate-900/70"
                      }`}
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex items-center gap-2.5">
                          <span className="rounded-md border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-blue-200">
                            {it.kategori}
                          </span>
                          <span
                            data-testid={`qc-item-status-${jo.jo_number}-${it.kategori}`}
                            className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300"
                          >
                            {it.status}
                          </span>
                          {it.photo_file_id && (
                            <button
                              data-testid={`qc-view-kat-photo-${jo.jo_number}-${it.kategori}`}
                              onClick={() => viewFile(it.photo_file_id)}
                              className="inline-flex items-center gap-1.5 text-xs text-emerald-300 hover:text-emerald-200 transition-colors duration-200"
                            >
                              <ImageIcon className="w-4 h-4" /> Foto
                            </button>
                          )}
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <button
                            data-testid={`qc-btn-diterima-${jo.jo_number}-${it.kategori}`}
                            onClick={() => setDiterima(jo, it.kategori)}
                            disabled={isBusy || done}
                            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-2.5 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-200 hover:bg-emerald-500/20 disabled:opacity-30 transition-colors duration-200"
                          >
                            {isBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PackageCheck className="w-3.5 h-3.5" />}
                            Diterima
                          </button>
                        </div>
                      </div>
                      <div
                        className="mt-2.5 border-t border-white/5 pt-2 space-y-1"
                        data-testid={`qc-item-history-${jo.jo_number}-${it.kategori}`}
                      >
                        {(jo.item_logs || []).filter((l) => l.kategori === it.kategori).length === 0 ? (
                          <div className="text-[10px] text-slate-600">Belum ada riwayat kategori ini</div>
                        ) : (
                          (jo.item_logs || [])
                            .filter((l) => l.kategori === it.kategori)
                            .slice(-5)
                            .reverse()
                            .map((l) => (
                              <div key={`kl-${l.at}-${l.status}`} className="flex items-center gap-2 text-[10px] text-slate-400">
                                <History className="w-3 h-3 text-sky-300 shrink-0" />
                                <span className="font-semibold text-slate-200">{l.status}</span>
                                oleh <span className="font-semibold text-sky-200">{l.by}</span>
                                <span className="text-slate-500">{stamp(l.at)}</span>
                              </div>
                            ))
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

            </div>
          );
        })}
      </div>
    </div>
  );
};
